import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AiOutlinePicture, AiOutlineEyeInvisible } from 'react-icons/ai';
import { apiUrl } from '../config/api';
import { timeAgo } from '../lib/timeAgo';
import './ActivityPage.css';

// The clock time a batch finished. The relative form ("2 hours ago") reads fast
// but can't be compared against a technician's timesheet, so show both.
function exactTime(value) {
  if (!value) return '\u2014';
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// A duration in milliseconds, written the way a person would say it.
function duration(ms) {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) {
    const secs = ms / 1000;
    return `${secs < 10 ? secs.toFixed(1) : Math.round(secs)}s`;
  }
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  if (mins < 60) return secs ? `${mins}m ${secs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

// How long these photos actually took to upload - transfer off the phone plus the
// write to S3 - not the gap between one capture and the next.
//
// Photos uploaded before the timing columns existed have none, so a partly-timed
// group says how many it is speaking for rather than implying it covers them all.
function uploadTiming(row) {
  const { uploadMsTotal, uploadMsAvg, timedPhotos, photos } = row;
  if (!timedPhotos || uploadMsTotal == null) return '';
  const summary = `${duration(uploadMsTotal)} to upload \u00b7 avg ${duration(uploadMsAvg)}`;
  return timedPhotos < photos ? `${summary} (${timedPhotos} of ${photos})` : summary;
}

// Admins and managers: who added photos, to which project, and when.
//
// Rows are grouped by person + project + how the photos arrived, rather than one
// row per photo - "Mike put 23 on Oak St yesterday" is the answer an admin wants,
// not 23 identical lines.
const PAGE_SIZE = 100;

// images.user_id does not mean the same thing on every upload path, so the page
// never says "uploaded by" for the two where it would be untrue. See the comment
// above ACTIVITY_SOURCE_SQL in backend/app/routers/auth.py.
const SOURCE_LABEL = {
  app: { text: 'Uploaded in app', className: 'act-tag--app' },
  share_link: { text: 'Via share link', className: 'act-tag--link' },
  imported: { text: 'Imported', className: 'act-tag--import' },
};

const SOURCE_NOTE = {
  share_link: 'Submitted through the public link. Recorded against the project owner — the sender has no account.',
  imported: 'Brought in from CompanyCam. Recorded against whoever ran the import, not the original photographer.',
};

const DATE_RANGES = [
  { id: '7', label: 'Last 7 days' },
  { id: '30', label: 'Last 30 days' },
  { id: '90', label: 'Last 90 days' },
  { id: '', label: 'All time' },
];

const ActivityPage = ({ user }) => {
  const token = localStorage.getItem('token');
  // Managers supervise uploads too, and the API grants them this route.
  const canView = !!(user?.isAdmin || user?.isManager);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [userId, setUserId] = useState('');
  const [days, setDays] = useState('30');
  const [page, setPage] = useState(0);

  // The project filter is a free-text match over what is already on screen rather
  // than another round trip - the row count here is small and it keeps typing instant.
  const [projectQuery, setProjectQuery] = useState('');

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
      if (userId) params.set('userId', userId);
      if (days) params.set('days', days);

      const res = await fetch(apiUrl(`/api/auth/activity?${params.toString()}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || data.message || 'Could not load activity.');
        return;
      }
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [canView, token, userId, days, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canView) return;
    fetch(apiUrl('/api/auth/users'), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setUsers(Array.isArray(list) ? list : []))
      .catch(() => { /* the filter is a convenience; the table works without it */ });
  }, [canView, token]);

  const visible = useMemo(() => {
    const q = projectQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.clientName || '').toLowerCase().includes(q));
  }, [rows, projectQuery]);

  const totals = useMemo(
    () => visible.reduce((sum, r) => sum + (r.photos || 0), 0),
    [visible]
  );

  if (!canView) {
    return (
      <div className="act-page">
        <div className="act-empty">This page is for administrators and managers.</div>
      </div>
    );
  }

  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="act-page">
      <div className="act-header">
        <div>
          <h1 className="act-title">Activity</h1>
          <p className="act-subtitle">Who added photos, to which project, and when.</p>
        </div>
      </div>

      <div className="act-filters">
        <select
          className="act-select"
          value={userId}
          onChange={(e) => { setUserId(e.target.value); setPage(0); }}
          aria-label="Filter by person"
        >
          <option value="">All people</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.name || u.email}</option>
          ))}
        </select>

        <select
          className="act-select"
          value={days}
          onChange={(e) => { setDays(e.target.value); setPage(0); }}
          aria-label="Filter by date"
        >
          {DATE_RANGES.map((r) => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>

        <input
          className="act-search"
          type="search"
          placeholder="Search projects on this page"
          value={projectQuery}
          onChange={(e) => setProjectQuery(e.target.value)}
          aria-label="Search projects"
        />
      </div>

      {error && <div className="act-error">{error}</div>}

      {loading ? (
        <div className="act-loading">Loading activity...</div>
      ) : visible.length === 0 ? (
        <div className="act-empty">
          {projectQuery
            ? 'No projects on this page match that search.'
            : 'No photos were added in this period.'}
        </div>
      ) : (
        <>
          <p className="act-count">
            {visible.length} {visible.length === 1 ? 'entry' : 'entries'} · {totals.toLocaleString()} photos
          </p>

          <div className="act-table-container">
            <table className="act-table">
              <thead>
                <tr>
                  <th>Who</th>
                  <th>Project</th>
                  <th>How</th>
                  <th className="act-num">Photos</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const tag = SOURCE_LABEL[r.source] || SOURCE_LABEL.imported;
                  return (
                    <tr key={`${r.userId}-${r.clientId}-${r.source}`}>
                      <td data-label="Who">
                        <span className="act-who">{r.userName || r.userEmail}</span>
                        {r.userName && <span className="act-who-email">{r.userEmail}</span>}
                      </td>
                      <td data-label="Project">{r.clientName}</td>
                      <td data-label="How">
                        <span className={`act-tag ${tag.className}`} title={SOURCE_NOTE[r.source] || ''}>
                          {tag.text}
                        </span>
                      </td>
                      <td data-label="Photos" className="act-num">
                        <span className="act-photos">
                          <AiOutlinePicture size={14} />
                          {r.photos}
                        </span>
                        {r.hidden > 0 && (
                          <span className="act-hidden" title={`${r.hidden} since removed`}>
                            <AiOutlineEyeInvisible size={13} />
                            {r.hidden}
                          </span>
                        )}
                      </td>
                      <td data-label="Uploaded" title={r.lastUpload ? new Date(r.lastUpload).toLocaleString() : ''}>
                        {/* One wrapper, so the mobile card's flex row sees a single
                            value next to its label instead of three competing items. */}
                        <span className="act-when-group">
                          <span className="act-when">{exactTime(r.lastUpload)}</span>
                          <span className="act-when-rel">{timeAgo(r.lastUpload)}</span>
                          {uploadTiming(r) && (
                            <span className="act-upload">{uploadTiming(r)}</span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="act-pager">
              <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>Page {page + 1} of {pages}</span>
              <button type="button" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActivityPage;
